import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Form, notification, Spin } from 'antd';
import { $error, AuthButton, DrawerFooter } from '@components';
import { bulkSetFormData, formatImageFiles } from '@gUtils';
import { withRouter } from 'react-router';
import { projectsAPI } from '@api';
import { exportBatchFailureCsv } from '@/utils/exportBatchFailureCsv';
import { exportBatchSuccessCsv } from '@/utils/exportBatchSuccessCsv';
import { runBatchSelfBuiltAssetCreate } from '@/utils/runBatchSelfBuiltAssetCreate';
import { AssetComment, AssetID, AssetImageCount, AssetName } from './form_renders';
import MultipleComponents from './MultipleComponents.jsx';
import AssociatedTask from './associated_task/index';

@withRouter
@inject('taskStore')
@observer
class TaskFormContent extends Component {
    formRef = React.createRef();

    state = {
        associatedPhotographers: 0,
        checkedTask: {},
        createMode: 'single',
        batchFolderParse: { items: [], skipped: [] },
        batchCreating: false
    };

    onBatchFolderParsed = (result) => {
        this.setState({
            batchFolderParse: result || { items: [], skipped: [] }
        });
    };

    updatePhotographers = (id) => {
        this.setState(() => ({
            associatedPhotographers: id
        }));
    };

    updateTaskCheck = (checkedTask) => {
        this.setState({
            checkedTask
        });
        const ids = checkedTask?.creator?.id;
        if (ids) {
            const photographers = Array.isArray(ids) ? ids : [ids];
            this.formRef?.current?.setFieldsValue({
                photographers
            });
        }
    };

    get store() {
        return this.props.taskStore;
    }

    get formType() {
        const { search } = this.props.location;
        return search.split('?')[1];
    }

    get currentTask() {
        return this.props.taskStore.currentTask;
    }

    get projectId() {
        return parseInt(this.props.match.params.id, 10);
    }

    get taskId() {
        return this.props.taskStore.currentTask.id;
    }

    componentDidMount() {
        this.store.fetchTaskConfigs();
        this.store.fetchPhotographers();
        if (this.taskId) {
            this.store.fetchAssetsInfo(this.taskId);
        }
        this.store.getReleatedAsset(this.projectId);
        this.store.isAssociated = false;
        this.store.isPlanningTask = false;

        if (this.formType === 'task_edit' && this.currentTask?.task_package_id) {
            this.formRef?.current?.setFieldsValue({
                package_id: this.currentTask?.task_package_id
            });
        }
        if (this.formType === 'task_edit' && this.currentTask?.task_package_item) {
            this.store.selectedItem = this.currentTask?.task_package_item;
            this.store.associatedTaskID = this.currentTask?.task_package_item?.id;
        }
        if (this.formType === 'task_edit' && this.currentTask?.asset_name) {
            this.formRef?.current?.setFieldsValue({
                asset_name: this.currentTask?.asset_name
            });
        }
    }

    componentWillUnmount() {
        this.store.setAssetCharacter(0);
        this.store.setAssetTitle('');
    }

    handleConfirm = async () => {
        const { pathname } = this.props.location;
        try {
            const formResult = await this.formRef.current.validateFields();
            const isBatchCreate = formResult.package_type === 2 && formResult.create_mode === 'batch';
            if (formResult.package_type === 1) {
                // 策划任务
                if (!this.store.selectedItem?.id) {
                    throw new Error('请选择任务包下的任务');
                }
            } else {
                if (isBatchCreate) {
                    this.setState({ batchCreating: true });
                    try {
                        const { items, skipped } = this.state.batchFolderParse || { items: [], skipped: [] };
                        if (!items.length) {
                            throw new Error('请先选择素材文件夹，且至少解析出一个有效资产');
                        }
                        const { ok, failed, succeeded, total } = await runBatchSelfBuiltAssetCreate({
                            items,
                            formResult,
                            projectId: this.projectId,
                            taskStore: this.store
                        });
                        this.props.updatePlanRelated('producer');
                        if (failed.length) {
                            exportBatchFailureCsv(failed);
                        }
                        if (formResult.export_success_ids && succeeded.length) {
                            exportBatchSuccessCsv(succeeded);
                        }
                        const skipNote = skipped.length > 0
                            ? `解析阶段另有 ${skipped.length} 条跳过/提示（未进入创建）。`
                            : '';
                        notification.info({
                            message: '批量创建结果',
                            description: (
                                <div>
                                    <div>
                                        本次共
                                        {' '}
                                        <strong>{total}</strong>
                                        {' '}
                                        个，成功
                                        {' '}
                                        <strong style={{ color: '#52c41a' }}>{ok}</strong>
                                        {' '}
                                        个，失败
                                        {' '}
                                        <strong style={{ color: failed.length ? '#faad14' : undefined }}>
                                            {failed.length}
                                        </strong>
                                        {' '}
                                        个。
                                    </div>
                                    {failed.length ? (
                                        <div style={{ marginTop: 8 }}>
                                            失败明细已自动下载为 CSV 表格（子文件夹名称、失败原因）。
                                        </div>
                                    ) : null}
                                    {formResult.export_success_ids && succeeded.length ? (
                                        <div style={{ marginTop: 8 }}>
                                            成功明细已自动下载为 JSON 文件（资产名称: 资产ID）。
                                        </div>
                                    ) : null}
                                    {skipNote ? (
                                        <div style={{ marginTop: 8, color: '#888' }}>{skipNote}</div>
                                    ) : null}
                                </div>
                            ),
                            duration: failed.length ? 10 : 5,
                            placement: 'topRight'
                        });
                        if (ok > 0 && !pathname.includes('dashboard')) {
                            this.props.history.push(
                                `/projects/${this.projectId}?task_flow?taskId=${this.store.taskID}`
                            );
                        }
                    } finally {
                        this.setState({ batchCreating: false });
                    }
                    return;
                }
                // 自建任务
                if (!Array.isArray(formResult.up_images) || formResult.up_images.length < 1) {
                    throw new Error('请上传图片文件');
                }
                let formData = new FormData();
                formData.append('package_id', formResult.package_id);
                formData.append('material_type', formResult.material_type);
                formData.append('title', formResult.title);
                formData = formatImageFiles(formResult.up_images, formData);
                formData.append('type', 2);
                const creatTask = await projectsAPI.associatedTask(formData);
                if (creatTask.code !== 1000) {
                    throw new Error(creatTask.msg || '创建任务失败');
                }
                this.store.associatedTaskID = creatTask.data.id;
            }
            delete formResult.up_images;
            delete formResult.create_mode;
            delete formResult.export_success_ids;
            const taskResult = await this.handleSave(formResult);
            if (taskResult.code === 1000) {
                const updateType = formResult.package_type === 1 ? 'plan' : 'producer';
                this.props.updatePlanRelated(updateType);
                if (!pathname.includes('dashboard')) {
                    this.props.history.push(
                        `/projects/${this.projectId}?task_flow?taskId=${this.taskId}`
                    );
                }
            } else {
                throw new Error(taskResult.msg || '操作失败');
            }
        } catch (error) {
            $error(error.message);
        }
    };

    handleSave = (params) => {
        let formData = new FormData();
        if (this.taskId) {
            formData.append('task_id', this.taskId);
        } else {
            formData.append('project_id', this.projectId);
        }
        formData.append('type', 0);
        if (this.store.assetsId) {
            formData.append('asset_id', this.store.assetsId);
        }
        if (this.store.associatedTaskID) {
            formData.append('package_item_id', this.store.associatedTaskID);
        }
        formData = bulkSetFormData(params, formData);
        if (this.formType === 'task_edit') {
            return this.store.updateTask(formData);
        }
        if (this.formType === 'task_add') {
            return this.store.createTask(formData);
        }
        return undefined;
    };

    // 表单字段变化时的name
    onValuesChange = (changedValues) => {
        const keyName = Object.keys(changedValues)[0];
        if (keyName === 'package_id' || keyName === 'package_type') {
            const patchValues = {
                asset_name: '',
                photographers: [],
                title: ''
            };
            if (keyName === 'package_type' && changedValues[keyName] !== 2) {
                patchValues.create_mode = 'single';
                this.setState({ createMode: 'single', batchFolderParse: { items: [], skipped: [] } });
            }
            if (keyName === 'package_id') {
                this.setState({ batchFolderParse: { items: [], skipped: [] } });
            }
            this.formRef?.current?.setFieldsValue({
                ...patchValues
            });
            this.updateTaskCheck({});
        }
        if (keyName === 'create_mode') {
            this.setState({ createMode: changedValues[keyName] || 'single' });
        }
        if (keyName === 'title') {
            this.formRef?.current?.setFieldsValue({
                asset_name: changedValues[keyName]
            });
        }
    };

    onCancel = () => {
        this.props.history.goBack();
    };

    renderForm = () => {
        const {
            asset_type,
            asset_category,
            photographers,
            currentTask,
            assetCharacter
        } = this.store;
        const {
            associatedPhotographers,
            checkedTask,
            createMode
        } = this.state;
        const isBatchCreate = this.formType === 'task_add' && createMode === 'batch';

        const props = {
            asset_type,
            asset_category,
            photographers,
            currentTask,
            assetCharacter,
            checkedTask,
            associatedPhotographers
        };
        return (
            <Form ref={this.formRef} onValuesChange={this.onValuesChange}>
                <div className="task-form">
                    <AssociatedTask
                        formType={this.formType}
                        currentTask={this.store.currentTask}
                        updatePhotographers={this.updatePhotographers}
                        updateTaskCheck={this.updateTaskCheck}
                        formRef={this.formRef}
                        onCreateModeChange={(mode) => this.setState({ createMode: mode || 'single' })}
                        onBatchFolderParsed={this.onBatchFolderParsed}
                    />
                    {!isBatchCreate && <AssetName {...props} formType={this.formType} formRef={this.formRef} />}
                    {!isBatchCreate && <AssetID currentTask={this.store.currentTask} />}
                    <MultipleComponents {...props} formType={this.formType} formRef={this.formRef} hideAssetType={isBatchCreate} />
                    <AssetImageCount {...props} />
                    <AssetComment currentTask={this.store.currentTask} />
                </div>
            </Form>
        );
    };

    renderButtons = () => {
        const { actionState } = this.store;
        const { batchCreating } = this.state;
        const confirmLoading = actionState === 'pending' || batchCreating;
        return (
            <DrawerFooter>
                <div style={{
                    position: 'fixed',
                    bottom: '20px'
                }}
                >
                    <AuthButton
                        type="primary"
                        className="mg-r-12"
                        onClick={this.handleConfirm}
                        loading={confirmLoading}
                        disabled={batchCreating}
                    >
                        确认
                    </AuthButton>
                    <AuthButton onClick={this.onCancel} disabled={batchCreating}>
                        取消
                    </AuthButton>
                </div>
            </DrawerFooter>
        );
    };

    render() {
        const { batchCreating } = this.state;
        return (
            <div style={{ position: 'relative' }}>
                {batchCreating ? (
                    <div
                        style={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: 10050,
                            background: 'rgba(255, 255, 255, 0.75)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'auto'
                        }}
                        aria-busy="true"
                        aria-live="polite"
                    >
                        <Spin size="large" tip="批量创建中，请稍候…" />
                    </div>
                ) : null}
                <h2 className="mg-b-42">
                    {this.formType === 'task_edit' ? '编辑' : '创建'}
                    资产
                </h2>
                {this.renderForm()}
                {this.renderButtons()}
            </div>
        );
    }
}

export default TaskFormContent;
